import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Receipt, Building2, MapPin, Clock, CreditCard, FileText, Eye, List, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useAccessibleLocations } from '../hooks/useAccessibleLocations';
import { REPORT_SHIFTS } from '../constants/reportFilters';
import { DateRangeQuickFilter } from '@/components/ui/date-range-quick-filter';
import { useDateRangeDefaults } from '@/hooks/useDateRangeDefaults';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

interface DetailedSalesFilters {
  locationId: string;
  shift: string;
  startDate: string;
  endDate: string;
  receiptStatus: string;
  receiptType: string;
}

interface SaleDetail {
  id: string;
  date: string;
  time: string;
  shift: string;
  companyName: string;
  locationName: string;
  products: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  totalAmount: number;
  paymentMethod: string;
  receiptType: string;
  receiptNumber: string;
  receiptStatus: 'emitido' | 'anulado' | 'pendiente';
  status?: string;
  customer?: string;
  customerDocument?: string;
  seller?: string;
}

interface ApiResponse {
  success: boolean;
  data: {
    sales: SaleDetail[];
    summary: {
      totalSales: number;
      totalAmount: number;
      averageTicket: number;
    };
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
      hasNext: boolean;
      hasPrev: boolean;
    };
  };
  meta: {
    generatedAt: string;
    locationId: string;
    filters: any;
  };
}


type ViewType = 'detailed' | 'general';

export const DetailedSalesReport: React.FC = () => {
  const { user } = useAuth();
  const [viewType, setViewType] = useState<ViewType>('general');
  const [loading, setLoading] = useState(false);
  const { locationsWithAll, defaultLocationId, loading: locationsLoading } = useAccessibleLocations();
  const [error, setError] = useState<string | null>(null);
  const [salesData, setSalesData] = useState<SaleDetail[]>([]);
  const [summary, setSummary] = useState({
    totalSales: 0,
    totalAmount: 0,
    averageTicket: 0
  });
  const { quickRange, dateFrom, dateTo, setQuickRange, setManualDates } = useDateRangeDefaults('detailedSalesReport');

  // Verificar si el usuario es CAJERO (positionId = 3)
  const isCashier = user?.positionId === 3;

  // Para CAJERO, configurar fechas del día actual automáticamente
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const [filters, setFilters] = useState<DetailedSalesFilters>({
    locationId: 'all',
    shift: 'all',
    startDate: isCashier ? today : '',
    endDate: isCashier ? today : '',
    receiptStatus: 'all',
    receiptType: 'all'
  });

  // Auto-seleccionar local cuando se carga
  useEffect(() => {
    if (defaultLocationId) {
      setFilters(prev => ({ ...prev, locationId: defaultLocationId }));
    }
  }, [defaultLocationId]);


  const updateFilter = (key: keyof DetailedSalesFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const fetchDetailedSalesData = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.shift !== 'all') queryParams.append('shift', filters.shift);
      if (filters.receiptStatus !== 'all') queryParams.append('receiptStatus', filters.receiptStatus);
      if (filters.receiptType !== 'all') queryParams.append('receiptType', filters.receiptType);

      const response = await fetch(`${API_BASE_URL}/reports/detailed-sales/${filters.locationId}?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar el reporte detallado');
      }

      const data: ApiResponse = await response.json();
      
      if (data.success) {
        setSalesData(data.data.sales);
        setSummary(data.data.summary);
      } else {
        throw new Error('Error en la respuesta del servidor');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      console.error('Error fetching detailed sales data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Sync date range with filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      startDate: dateFrom,
      endDate: dateTo
    }));
  }, [dateFrom, dateTo]);

  // Auto-load data for CAJERO users (wait for locationId to be set)
  useEffect(() => {
    if (isCashier && filters.startDate && filters.endDate && filters.locationId && filters.locationId !== 'all') {
      fetchDetailedSalesData();
    }
  }, [isCashier, filters.startDate, filters.endDate, filters.locationId]);

  const handleDateChange = (key: string, value: string) => {
    if (key === 'startDate') {
      setManualDates(value, dateTo);
      updateFilter('startDate', value);
    } else if (key === 'endDate') {
      setManualDates(dateFrom, value);
      updateFilter('endDate', value);
    }
  };

  const handleApplyFilters = () => {
    fetchDetailedSalesData();
  };

  const handleExportPDF = () => {
    try {
      const exportData = salesData.map(sale => [
        sale.date,
        sale.time,
        sale.shift,
        sale.locationName,
        sale.products.map(p => `${p.name} (Cant: ${p.quantity}, Subtotal: S/ ${p.total.toFixed(2)})`).join(', '),
        `S/ ${sale.totalAmount.toFixed(2)}`,
        sale.paymentMethod,
        sale.receiptType,
        sale.receiptNumber,
        sale.customerDocument || 'N/A',
        sale.customer || 'N/A'
      ]);

      console.log('PDF Export Data:', exportData);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
    }
  };

  const handleExportExcel = () => {
    try {
      const exportData = salesData.map(sale => [
        sale.date,
        sale.time,
        sale.shift,
        sale.companyName,
        sale.locationName,
        sale.products.map(p => `${p.name} (Cant: ${p.quantity}, Subtotal: S/ ${p.total.toFixed(2)})`).join(', '),
        sale.totalAmount,
        sale.paymentMethod,
        sale.receiptType,
        sale.receiptNumber,
        sale.customerDocument || 'N/A',
        sale.customer || 'N/A'
      ]);

      console.log('Excel Export Data:', exportData);
    } catch (error) {
      console.error('Error al exportar Excel:', error);
    }
  };

  const formatTime = (time: string) => {
    return time.slice(0, 5);
  };

  const getPaymentMethodColor = (method: string) => {
    switch (method) {
      case 'Efectivo': return 'bg-green-100 text-green-800';
      case 'Tarjeta': return 'bg-blue-100 text-blue-800';
      case 'Aplicativo': return 'bg-yellow-100 text-yellow-800';
      case 'Yape': return 'bg-purple-100 text-purple-800';
      case 'Plin': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReceiptTypeColor = (type: string) => {
    switch (type) {
      case 'Boleta': return 'bg-purple-100 text-purple-800';
      case 'Factura': return 'bg-indigo-100 text-indigo-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReceiptStatusColor = (status: string) => {
    switch (status) {
      case 'emitido': return 'bg-green-100 text-green-800';
      case 'anulado': return 'bg-red-100 text-red-800';
      case 'pendiente': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getReceiptStatusText = (status: string) => {
    switch (status) {
      case 'emitido': return 'Emitido';
      case 'anulado': return 'Anulado';
      case 'pendiente': return 'Pendiente';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Reporte Detallado de Ventas</h2>
      </div>

      {/* Filtros - Solo mostrar para usuarios que NO son CAJERO */}
      {!isCashier && (
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Local</Label>
                <Select
                  value={filters.locationId}
                  onValueChange={(value) => updateFilter('locationId', value)}
                  disabled={locationsLoading}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue>
                      {locationsLoading ? 'Cargando...' : locationsWithAll.find(l => l.id === filters.locationId)?.nombre || 'Seleccionar'}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {locationsWithAll.map(location => (
                      <SelectItem key={location.id} value={location.id}>
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2" />
                          {location.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Rango</Label>
                <DateRangeQuickFilter
                  value={quickRange}
                  onChange={setQuickRange}
                  reportType="detailedSales"
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Desde</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => updateFilter('startDate', e.target.value)}
                  className="w-[140px]"
                  disabled={quickRange !== 'custom'}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Hasta</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => updateFilter('endDate', e.target.value)}
                  className="w-[140px]"
                  disabled={quickRange !== 'custom'}
                />
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Turno</Label>
                <Select value={filters.shift} onValueChange={(value) => updateFilter('shift', value)}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REPORT_SHIFTS.map(shift => (
                      <SelectItem key={shift.id} value={shift.id}>{shift.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Comprobante</Label>
                <Select value={filters.receiptType} onValueChange={(value) => updateFilter('receiptType', value)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="Boleta">Boleta</SelectItem>
                    <SelectItem value="Factura">Factura</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Estado</Label>
                <Select value={filters.receiptStatus} onValueChange={(value) => updateFilter('receiptStatus', value)}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="emitido">Emitido</SelectItem>
                    <SelectItem value="anulado">Anulado</SelectItem>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={fetchDetailedSalesData}
                disabled={loading}
                className="bg-macartur-primary hover:bg-macartur-primary/90"
              >
                {loading ? 'Cargando...' : 'Generar Reporte'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Message */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-4">
            <div className="flex items-center text-red-800">
              <Receipt className="w-5 h-5 mr-2" />
              <div>
                <p className="font-medium">Error al cargar el reporte</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin mr-3" />
              <span>Cargando reporte detallado...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {!loading && !error && (
        <>

          {/* Resumen estadístico */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-blue-800">Total de Ventas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-900">{summary.totalSales}</div>
                <p className="text-xs text-blue-700">Transacciones individuales</p>
              </CardContent>
            </Card>
            
            <Card className="border-green-200 bg-green-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-green-800">Monto Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-900">
                  S/ {summary.totalAmount.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-green-700">Suma de todas las ventas</p>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-purple-800">Ticket Promedio</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-900">
                  S/ {summary.averageTicket.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                </div>
                <p className="text-xs text-purple-700">Valor promedio por venta</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabla con vista condicional */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {viewType === 'detailed' ? 'Detalle de Ventas Individuales' : 'Vista General de Ventas'}
                </CardTitle>
                
                {/* Toggle de vista */}
                <div className="flex items-center gap-2 border rounded-lg p-1">
                  <Button
                    variant={viewType === 'general' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewType('general')}
                    className="h-8"
                  >
                    <List className="w-4 h-4 mr-2" />
                    General
                  </Button>
                  <Button
                    variant={viewType === 'detailed' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewType('detailed')}
                    className="h-8"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Detallada
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha/Hora</TableHead>
                      <TableHead>Turno</TableHead>
                      <TableHead>Local</TableHead>
                      {viewType === 'detailed' && <TableHead>Productos</TableHead>}
                      <TableHead>Total</TableHead>
                      <TableHead>Pago</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead># Comprobante</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Cliente</TableHead>
                      {viewType === 'detailed' && <TableHead>Vendedor</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesData.map((sale) => (
                      <TableRow key={sale.id} className={sale.status === 'ANULADA' ? 'bg-red-50 opacity-60' : ''}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{sale.date}</span>
                            <span className="text-sm text-gray-500">{formatTime(sale.time)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {sale.shift}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1 text-gray-400" />
                            <span className="text-sm">{sale.locationName}</span>
                          </div>
                        </TableCell>
                        {viewType === 'detailed' && (
                          <TableCell>
                            <div className="space-y-1">
                              {sale.products.map((product, idx) => (
                                <div key={idx} className="text-sm">
                                  <div className="flex flex-col">
                                    <span className="font-medium">{product.name}</span>
                                    <div className="text-gray-500 text-xs">
                                      <span>Cant: {product.quantity}</span>
                                      <span className="mx-1">•</span>
                                      <span>Subtotal: S/ {product.total.toFixed(2)}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <span className={`font-bold ${sale.status === 'ANULADA' ? 'line-through text-red-500' : ''}`}>
                            S/ {sale.totalAmount.toFixed(2)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getPaymentMethodColor(sale.paymentMethod)}>
                            <CreditCard className="w-3 h-3 mr-1" />
                            {sale.paymentMethod}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getReceiptTypeColor(sale.receiptType)}>
                            <FileText className="w-3 h-3 mr-1" />
                            {sale.receiptType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-mono">{sale.receiptNumber}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={getReceiptStatusColor(sale.receiptStatus)}>
                            {getReceiptStatusText(sale.receiptStatus)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{sale.customerDocument || 'N/A'}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{sale.customer || 'N/A'}</span>
                        </TableCell>
                        {viewType === 'detailed' && (
                          <TableCell>
                            <span className="text-sm">{sale.seller || 'N/A'}</span>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {salesData.length === 0 && !loading && (
                <div className="text-center py-8">
                  <Receipt className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No se encontraron ventas que coincidan con los filtros aplicados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};